// #tag: ADMIT CARDS AND HALL TICKETS SCREEN
// #purpose: Displays downloadable examination admit cards, status filters (released, expected, delayed), and scheduled test dates.
import React, { useState } from 'react';
import { Download, Search } from 'lucide-react';
import { FilterChips } from '../components/common/Cards';
import { PageHeaderSection } from '../components/cms/PageSections';
import { EmptyState, ErrorState, LoadingState } from '../components/states/StateViews';
import { useSearchSounds } from '../hooks/useSearchSounds';
import { useAdmitCardStatuses, useAdmitCards, useExamTypes, usePageSections } from '../hooks/useExamData';
import type { AdmitCard, ExamCategory } from '../types';

function AdmitCardItem({ card, statusLabel }: { card: AdmitCard; statusLabel: string }) {
  return (
    <article className={`wfx-admit-card glass-surface status-${card.status}`}>
      <div className="wfx-admit-card-head">
        <span className="chip-pill">{card.examCategory}</span>
        <span className={`wfx-status-badge status-${card.status}`}>{statusLabel}</span>
      </div>
      <h3>{card.title}</h3>
      <p className="wfx-muted">{card.organization}</p>
      <div className="wfx-admit-meta">
        {card.releaseDate && <span>Release: {card.releaseDate}</span>}
        {card.examDate && <span>Exam: {card.examDate}</span>}
      </div>
      {card.status === 'released' && (
        <button type="button" className="primary-action compact">
          <Download size={16} /> Download admit card
        </button>
      )}
    </article>
  );
}

export function AdmitCardsScreen() {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [examFilter, setExamFilter] = useState<string>('all');

  const { data: sectionsData } = usePageSections('admit-cards');
  const headerSection = sectionsData?.data?.[0];
  const { data: statusesData } = useAdmitCardStatuses();
  const { data: examTypesData } = useExamTypes();
  const examTypeNames = (examTypesData?.data ?? []).map((t) => t.name);

  const { data, isLoading, isError, refetch } = useAdmitCards({
    q: query || undefined,
    status: status === 'all' ? undefined : status,
    examCategory: examFilter === 'all' ? undefined : examFilter,
  });

  const cards = data?.data ?? [];
  const total = data?.pagination?.total ?? cards.length;
  const { onSearchKeyDown } = useSearchSounds(query, total);

  if (isLoading && !data) {
    return (
      <section className="adaptive-page">
        <LoadingState label="Loading admit cards…" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="adaptive-page">
        <ErrorState message="Could not load admit cards." onRetry={() => refetch()} playSound={false} />
      </section>
    );
  }

  const statusLabels = Object.fromEntries((statusesData?.data ?? []).map((s) => [s.id, s.label]));

  return (
    <section className="adaptive-page">
      {headerSection && <PageHeaderSection section={headerSection} />}

      <div className="wfx-search-bar glass-surface">
        <Search size={20} aria-hidden="true" />
        <input
          type="search"
          placeholder="Search admit cards…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          aria-label="Search admit cards"
        />
      </div>

      <div className="wfx-filter-section">
        <span className="wfx-filter-label">Status</span>
        <div className="wfx-filter-chips" role="group">
          {(statusesData?.data ?? []).map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`chip-pill ${status === tab.id ? 'is-active' : ''}`}
              onClick={() => setStatus(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wfx-filter-section">
        <span className="wfx-filter-label">Exam type</span>
        <FilterChips
          options={examTypeNames as ExamCategory[]}
          selected={examFilter as ExamCategory | 'all'}
          onChange={(v) => setExamFilter(v)}
        />
      </div>

      {cards.length === 0 ? (
        <EmptyState title="No admit cards match your filters" illustrationContext="empty.admit-cards" />
      ) : (
        <div className="wfx-result-grid">
          {cards.map((card) => (
            <AdmitCardItem key={card.id} card={card} statusLabel={statusLabels[card.status] ?? card.status} />
          ))}
        </div>
      )}
    </section>
  );
}
